import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const STORAGE_KEY = "ls_cookie_prefs_v1";

export const CookieBanner = () => {
    const [visible, setVisible] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({ necessary: true, analytics: false });

    useEffect(() => {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) setVisible(true);
    }, []);

    const save = (choice) => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ ...choice, ts: new Date().toISOString() })
        );
        setVisible(false);
    };

    const acceptAll = () => save({ necessary: true, analytics: true });
    const declineOptional = () =>
        save({ necessary: true, analytics: false });
    const saveSelection = () => save(prefs);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 80, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 80, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="fixed bottom-4 left-4 right-4 md:left-8 md:right-8 md:bottom-8 z-[60]"
                    data-testid="cookie-banner"
                >
                    <div className="max-w-4xl mx-auto bg-ls-ink text-ls-bg p-6 md:p-8 shadow-2xl border border-ls-ink/30 rounded-sm">
                        {!showPrefs ? (
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                                <div className="md:col-span-8">
                                    <h4 className="font-serif text-2xl md:text-3xl mb-2 leading-tight">
                                        Diese Website verwendet Cookies.
                                    </h4>
                                    <p className="font-sans text-sm text-ls-bg/75 leading-relaxed">
                                        Wir verwenden notwendige Cookies für den
                                        Betrieb dieser Website. Optionale Cookies
                                        helfen uns, unser Angebot zu verbessern.
                                        Details finden Sie in unserer{" "}
                                        <Link
                                            to="/datenschutz"
                                            className="underline underline-offset-2 hover:text-white"
                                        >
                                            Datenschutzerklärung
                                        </Link>
                                        .
                                    </p>
                                </div>
                                <div className="md:col-span-4 flex flex-col gap-3">
                                    <button
                                        onClick={acceptAll}
                                        className="w-full bg-ls-bg text-ls-ink px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans hover:bg-white transition-colors rounded-full"
                                        data-testid="cookie-accept-all"
                                    >
                                        Alle akzeptieren
                                    </button>
                                    <button
                                        onClick={declineOptional}
                                        className="w-full border border-ls-bg/40 text-ls-bg px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans hover:bg-ls-bg/10 transition-colors rounded-full"
                                        data-testid="cookie-decline"
                                    >
                                        Nur notwendige
                                    </button>
                                    <button
                                        onClick={() => setShowPrefs(true)}
                                        className="text-xs font-sans underline underline-offset-2 text-ls-bg/70 hover:text-ls-bg self-center"
                                        data-testid="cookie-preferences"
                                    >
                                        Einstellungen anpassen
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <h4 className="font-serif text-2xl md:text-3xl mb-6">
                                    Cookie-Einstellungen
                                </h4>
                                <div className="space-y-4 mb-8">
                                    <PrefRow
                                        title="Notwendige Cookies"
                                        desc="Erforderlich für Grundfunktionen der Website (Sicherheit, Präferenzen). Nicht deaktivierbar."
                                        checked
                                        disabled
                                    />
                                    <PrefRow
                                        title="Analyse & Statistik"
                                        desc="Helfen uns zu verstehen, wie die Website genutzt wird. Nur mit Ihrer Einwilligung."
                                        checked={prefs.analytics}
                                        onChange={(v) =>
                                            setPrefs((p) => ({
                                                ...p,
                                                analytics: v,
                                            }))
                                        }
                                        testid="cookie-pref-analytics"
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                    <button
                                        onClick={() => setShowPrefs(false)}
                                        className="px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans text-ls-bg/70 hover:text-ls-bg"
                                        data-testid="cookie-prefs-back"
                                    >
                                        Zurück
                                    </button>
                                    <button
                                        onClick={saveSelection}
                                        className="bg-ls-bg text-ls-ink px-6 py-3 text-xs uppercase tracking-[0.22em] font-sans hover:bg-white transition-colors rounded-full"
                                        data-testid="cookie-prefs-save"
                                    >
                                        Auswahl speichern
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const PrefRow = ({ title, desc, checked, disabled, onChange, testid }) => (
    <div className="flex items-start justify-between gap-6 border-t border-ls-bg/15 pt-4">
        <div>
            <div className="font-sans text-sm font-medium">{title}</div>
            <div className="font-sans text-xs text-ls-bg/65 mt-1 max-w-md">
                {desc}
            </div>
        </div>
        <label
            className={`relative inline-block w-10 h-5 rounded-full flex-shrink-0 ${
                disabled ? "opacity-70" : "cursor-pointer"
            } ${checked ? "bg-ls-bg" : "bg-ls-bg/20"}`}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange && onChange(e.target.checked)}
                className="sr-only"
                data-testid={testid}
            />
            <span
                className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                    checked
                        ? "left-[22px] bg-ls-ink"
                        : "left-0.5 bg-ls-bg"
                }`}
            />
        </label>
    </div>
);

export default CookieBanner;
